// Esqueleto del `#app`: mapa a pantalla completa, botón de ubicación y un panel inferior con tres
// pestañas (Cerca, Líneas, Ruta) más una vista de detalle (parada o línea) que se superpone a
// la pestaña con un botón «‹» para volver. Solo DOM: no conoce datos ni planificador.

export interface Shell {
  map: HTMLElement;
  loc: HTMLButtonElement;
  cfg: HTMLButtonElement;
  msg: HTMLDivElement;
  sheet: HTMLDivElement;
  grip: HTMLDivElement;
  tabNear: HTMLButtonElement;
  tabLines: HTMLButtonElement;
  tabRoute: HTMLButtonElement;
  pNear: HTMLDivElement;
  pLines: HTMLDivElement;
  pRoute: HTMLDivElement;
  pDetail: HTMLDivElement;
  oTxt: HTMLInputElement;
  dTxt: HTMLInputElement;
  oGps: HTMLButtonElement;
  oPick: HTMLButtonElement;
  dPick: HTMLButtonElement;
  res: HTMLDivElement;
  dlg: HTMLDivElement;
  gkey: HTMLInputElement;
  gcancel: HTMLButtonElement;
  gok: HTMLButtonElement;
  gdel: HTMLButtonElement;
  gabout: HTMLButtonElement;
}

const html = String.raw;

export function mountShell(root: HTMLElement): Shell {
  root.innerHTML = html`
    <div id="map" role="application" aria-label="Mapa de líneas de bus"></div>

    <button id="loc" type="button" aria-label="Mi ubicación">
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
        <circle cx="12" cy="12" r="4" fill="currentColor" />
        <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="2" />
        <path d="M12 1v3M12 20v3M1 12h3M20 12h3" stroke="currentColor" stroke-width="2" />
      </svg>
    </button>
    <div id="msg" role="status" aria-live="polite"></div>

    <div id="dlg" role="dialog" aria-modal="true" aria-labelledby="dlg-title">
      <div>
        <h2 id="dlg-title">Fondo Google Maps</h2>
        <p>
          Pega tu clave de la API de Google Maps (con <b>Maps JavaScript API</b> activada). Se
          guarda solo en este dispositivo. Sin clave se usa el mapa de OpenStreetMap.
        </p>
        <input id="gkey" placeholder="AIza..." autocomplete="off" spellcheck="false" />
        <div class="row">
          <button id="gdel" type="button">Quitar</button>
          <button id="gcancel" type="button">Cancelar</button>
          <button id="gok" type="button" class="ok">Guardar</button>
        </div>
        <div class="row" style="margin-top:6px">
          <button id="gabout" type="button">Acerca de</button>
        </div>
      </div>
    </div>

    <div id="sheet">
      <div id="grip" aria-label="Plegar o desplegar el panel"><i></i></div>
      <header id="head">
        <h1>Bus <b>Elx</b></h1>
        <button id="cfg" type="button" aria-label="Ajustes">
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
            <path
              fill="currentColor"
              d="M19.4 13a7.5 7.5 0 0 0 0-2l2.1-1.6-2-3.4-2.5 1a7.6 7.6 0 0 0-1.7-1L15 3.3h-4l-.4 2.7a7.6 7.6 0 0 0-1.7 1l-2.5-1-2 3.4L6.6 11a7.5 7.5 0 0 0 0 2l-2.1 1.6 2 3.4 2.5-1a7.6 7.6 0 0 0 1.7 1l.4 2.7h4l.4-2.7a7.6 7.6 0 0 0 1.7-1l2.5 1 2-3.4zM13 15.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7z"
            />
          </svg>
        </button>
      </header>
      <nav id="tabs" role="tablist">
        <button data-t="near" class="act" type="button" role="tab">Cerca</button>
        <button data-t="lines" type="button" role="tab">Líneas</button>
        <button data-t="route" type="button" role="tab">Ruta</button>
      </nav>
      <div id="body">
        <div id="pNear" class="panel"></div>
        <div id="pLines" class="panel" hidden></div>
        <div id="pRoute" class="panel" hidden>
          <div class="od">
            <em>A</em>
            <input id="oTxt" placeholder="Origen: dirección o mi ubicación" />
            <button id="oGps" type="button" title="Mi ubicación">◎</button>
            <button id="oPick" type="button" title="Elegir en el mapa">⌖</button>
          </div>
          <div class="od">
            <em class="d">B</em>
            <input id="dTxt" placeholder="Destino: dirección o toca el mapa" />
            <button id="dPick" type="button" title="Elegir en el mapa">⌖</button>
          </div>
          <div id="res"></div>
        </div>
        <div id="pDetail" class="panel" hidden></div>
      </div>
    </div>
  `;

  const q = <T extends HTMLElement>(sel: string): T => {
    const el = root.querySelector<T>(sel);
    if (!el) throw new Error(`No encuentro en el shell: ${sel}`);
    return el;
  };
  const tabs = root.querySelectorAll<HTMLButtonElement>("#tabs button");
  return {
    map: q<HTMLElement>("#map"),
    loc: q<HTMLButtonElement>("#loc"),
    cfg: q<HTMLButtonElement>("#cfg"),
    msg: q<HTMLDivElement>("#msg"),
    sheet: q<HTMLDivElement>("#sheet"),
    grip: q<HTMLDivElement>("#grip"),
    tabNear: tabs[0]!,
    tabLines: tabs[1]!,
    tabRoute: tabs[2]!,
    pNear: q<HTMLDivElement>("#pNear"),
    pLines: q<HTMLDivElement>("#pLines"),
    pRoute: q<HTMLDivElement>("#pRoute"),
    pDetail: q<HTMLDivElement>("#pDetail"),
    oTxt: q<HTMLInputElement>("#oTxt"),
    dTxt: q<HTMLInputElement>("#dTxt"),
    oGps: q<HTMLButtonElement>("#oGps"),
    oPick: q<HTMLButtonElement>("#oPick"),
    dPick: q<HTMLButtonElement>("#dPick"),
    res: q<HTMLDivElement>("#res"),
    dlg: q<HTMLDivElement>("#dlg"),
    gkey: q<HTMLInputElement>("#gkey"),
    gcancel: q<HTMLButtonElement>("#gcancel"),
    gok: q<HTMLButtonElement>("#gok"),
    gdel: q<HTMLButtonElement>("#gdel"),
    gabout: q<HTMLButtonElement>("#gabout"),
  };
}

// Toast breve, no bloqueante.
export function showToast(msg: HTMLDivElement, text: string, ms = 5000): void {
  msg.textContent = text;
  msg.classList.add("show");
  window.setTimeout(() => msg.classList.remove("show"), ms);
}
